/**
 * Self-serve advertiser create schemas (WEB-40).
 *
 * Reuses the M1 admin advertiser input shape so there is a single Zod
 * contract for creating an `Advertiser` row — admin-mediated create
 * (`adminSponsoredAdvertiserInputSchema`) and the self-serve onboarding
 * organization step share the same field rules and email normalization.
 */
import { z } from 'zod';

import { adminSponsoredAdvertiserInputSchema } from './admin/sponsored';

/**
 * Body for `POST /api/v1/advertisers` (self-serve onboarding org step).
 * Identical to the admin inline-advertiser payload by design.
 */
export const createAdvertiserSchema = adminSponsoredAdvertiserInputSchema;
export type CreateAdvertiserBody = z.infer<typeof createAdvertiserSchema>;

/**
 * Wizard draft persisted client-side across refreshes (WEB-40 AC).
 *
 * Intentionally LOOSE — mid-wizard refresh must restore partial / invalid
 * typed values (empty name, half-typed email). Submit validation still uses
 * `createAdvertiserSchema`. `taxId` is draft-only; Stripe Tax is authoritative
 * at Checkout and this field is never POSTed to `/advertisers`.
 */
export const advertiseOrganizationDraftSchema = z
  .object({
    name: z.string().max(200).default(''),
    contactEmail: z.string().max(320).default(''),
    website: z.string().max(500).optional().default(''),
    contactPhone: z.string().max(50).optional().default(''),
    taxId: z.string().max(32).optional().default(''),
  })
  .passthrough();
export type AdvertiseOrganizationDraft = z.infer<typeof advertiseOrganizationDraftSchema>;

/**
 * Client form schema — required name + contact email (AC1); optional website,
 * phone, and draft tax id. Submit strips `taxId` before calling the API.
 */
export const advertiseOrganizationFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Organization name is required').max(200),
    contactEmail: z.string().trim().toLowerCase().email('Enter a valid contact email').max(320),
    website: z
      .string()
      .trim()
      .max(500)
      .url('Enter a valid URL')
      .optional()
      .or(z.literal('').transform(() => undefined)),
    contactPhone: z
      .string()
      .trim()
      .max(50)
      .optional()
      .or(z.literal('').transform(() => undefined)),
    taxId: z
      .string()
      .trim()
      .max(32)
      .optional()
      .or(z.literal('').transform(() => undefined)),
  })
  .strict();
export type AdvertiseOrganizationFormValues = z.infer<typeof advertiseOrganizationFormSchema>;
